import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  useProjectExtras,
  useCreateExtra,
  useSendExtraForm,
  type ProjectExtra,
} from "@/hooks/use-project-extras";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useExtraContracts, contractsByExtraId } from "@/hooks/use-extra-contracts";
import { formatNOK } from "@/lib/format";
import ExtrasPaymentBatchButton from "./ExtrasPaymentBatchButton";
import { toast } from "sonner";
import type { DBInvoice } from "@/lib/db-types";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import StatisterTable from "./StatisterTable";
import { StatisterExportButton } from "./StatisterExportButton";
import { SrIcon } from "@/components/ui/SrIcon";

export interface ExtraForm {
  full_name: string;
  role: string;
  email: string;
  phone_number: string;
  address: string;
  personal_number: string;
  bank_account: string;
  budget_line_id: string;
  payment_method: "lonnet" | "faktura";
  agreed_amount: string;
}

const EMPTY_FORM: ExtraForm = {
  full_name: "",
  role: "",
  email: "",
  phone_number: "",
  address: "",
  personal_number: "",
  bank_account: "",
  budget_line_id: "",
  payment_method: "lonnet",
  agreed_amount: "",
};

/** Maks antall statister som kan velges per batch-send. */
const MAX_BATCH_SIZE = 50;

interface StatisterTabProps {
  projectId: string;
  /** VIB-2398: brukes kun i CSV-filnavnet. Faller tilbake på projectId. */
  projectCode?: string | null;
  invoices?: DBInvoice[];
  budgetLines?: BudgetLineRow[];
}

export default function StatisterTab({
  projectId,
  projectCode,
  invoices = [],
  budgetLines = [],
}: StatisterTabProps) {
  const { activeUser: user } = useImpersonation();
  // VIB-722c: manage_extras gates Legg-til / batch-send-knapper.
  // Admin/superadmin overstyrer; default = kun project_lead=true.
  const canManageExtras = useProjectPermission(projectId, "manage_extras");
  const { data: extras, isLoading } = useProjectExtras(projectId);
  // VIB-1281: kontrakt-knappene er bak feature_contracts (default off, jf.
  // flaggplan bølge 8) — rolle-feltet (VIB-1387) er IKKE gatet.
  const showContracts = useFeatureFlag("feature_contracts");
  const { data: contracts } = useExtraContracts(showContracts ? projectId : undefined);
  const contractMap = contractsByExtraId(contracts);
  const createMut = useCreateExtra();
  const sendFormMut = useSendExtraForm();
  const [adding, setAdding] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [form, setForm] = useState<ExtraForm>({ ...EMPTY_FORM });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchSending, setBatchSending] = useState(false);

  const handleAdd = () => {
    if (!form.full_name) return;
    // VIB-675: ikke send skjema automatisk. PL trigger send manuelt per rad
    // eller batcher flere stykker. autoSendForm utelates → default false.
    createMut.mutate(
      {
        project_id: projectId,
        full_name: form.full_name,
        role: form.role || undefined,
        email: form.email || undefined,
        phone_number: form.phone_number || undefined,
        personal_number: form.personal_number || undefined,
        bank_account: form.bank_account || undefined,
        agreed_amount: form.agreed_amount ? parseFloat(form.agreed_amount) : undefined,
        budget_line_id: form.budget_line_id || null,
      },
      {
        onSuccess: () => {
          setForm({ ...EMPTY_FORM });
          setAdding(false);
        },
      }
    );
  };

  const selectableExtras: ProjectExtra[] = (extras ?? []).filter((e) => !!e.email);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableExtras.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableExtras.map((e) => e.id)));
    }
  };

  const handleBatchSend = async () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size > MAX_BATCH_SIZE) {
      toast.error(`Maks ${MAX_BATCH_SIZE} rader kan sendes per batch. Du har valgt ${selectedIds.size}.`);
      return;
    }
    if (!user?.id) {
      toast.error("Mangler bruker-ID");
      return;
    }

    setBatchSending(true);
    let ok = 0;
    let failed = 0;
    const ids = Array.from(selectedIds);

    // Sekvensiell loop: hver mutateAsync utløser supabase.functions.invoke +
    // status-update. Parallell ville gitt rate-limit-risiko mot Resend og er
    // ikke verdt kompleksiteten for ≤50 rader.
    for (const id of ids) {
      try {
        await sendFormMut.mutateAsync({ extra_id: id, user_id: user.id, project_id: projectId });
        ok += 1;
      } catch (err) {
        console.warn("Batch-send feilet for", id, err);
        failed += 1;
      }
    }

    setBatchSending(false);
    setSelectedIds(new Set());

    if (failed === 0) {
      toast.success(`${ok} skjema sendt`);
    } else {
      toast.warning(`${ok} sendt, ${failed} feilet — sjekk status per rad`);
    }
  };

  const totalCost = (extras ?? []).reduce((s, e) => s + (e.agreed_amount ?? 0), 0);
  const count = (extras ?? []).length;
  const incompleteCount = (extras ?? []).filter(
    (e) => !(e.full_name && e.address && e.personal_number && e.bank_account)
  ).length;

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      {/* Collapsible header */}
      <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors rounded-t-xl">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <SrIcon name="users2" className="w-5 h-5 text-primary"  />
          <div>
            <h3 className="font-display text-lg">Statister / Lønnet ekstern</h3>
            <p className="text-[11px] text-muted-foreground font-normal">Statister, frilansere, og annet lønnet arbeid utført av eksterne</p>
          </div>
          {count > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {count}
            </span>
          )}
          {incompleteCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
              {incompleteCount} mangler info
            </span>
          )}
          {totalCost > 0 && (
            <span className="text-sm font-medium text-muted-foreground ml-2">
              {formatNOK(totalCost)}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {!collapsed && count > 0 && (
            <>
              <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                <Link to={`/prosjekter/${projectId}/statister/print`} target="_blank" rel="noopener">
                  <SrIcon name="file-text" className="w-3 h-3 mr-1" /> Print liste
                </Link>
              </Button>
              <StatisterExportButton
                filenameKey={projectCode || projectId}
                extras={extras ?? []}
              />
            </>
          )}
          {!collapsed && canManageExtras && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setAdding(true)}
              disabled={adding}
            >
              <SrIcon name="plus" className="w-3 h-3 mr-1"  /> Legg til
            </Button>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1">
            {collapsed ? (
              <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground"  />
            ) : (
              <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground"  />
            )}
          </button>
        </div>
      </div>

      {/* Batch-send toolbar */}
      {!collapsed && canManageExtras && selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-t bg-primary/5">
          <span className="text-xs text-muted-foreground">
            {selectedIds.size} valgt
            {selectedIds.size > MAX_BATCH_SIZE && (
              <span className="text-destructive ml-2">(maks {MAX_BATCH_SIZE} per batch)</span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setSelectedIds(new Set())}
              disabled={batchSending}
            >
              Fjern markering
            </Button>
            <ExtrasPaymentBatchButton
              projectId={projectId}
              canManage={canManageExtras}
              extras={extras ?? []}
              selectedIds={selectedIds}
              onDone={() => setSelectedIds(new Set())}
            />
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleBatchSend}
              disabled={batchSending || selectedIds.size > MAX_BATCH_SIZE}
            >
              {batchSending ? (
                <>
                  <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
                  Sender...
                </>
              ) : (
                <>
                  <SrIcon name="send" className="w-3 h-3 mr-1" />
                  Send til alle markerte ({selectedIds.size})
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {!collapsed && (
        <StatisterTable
          projectId={projectId}
          extras={extras ?? []}
          isLoading={isLoading}
          userId={user?.id ?? null}
          invoices={invoices}
          budgetLines={budgetLines}
          adding={adding}
          onSetAdding={setAdding}
          form={form}
          onSetForm={setForm}
          onAdd={handleAdd}
          createMutIsPending={createMut.isPending}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          selectableCount={selectableExtras.length}
          showContracts={showContracts}
          contractMap={contractMap}
        />
      )}
    </div>
  );
}
